import { supabase } from "@/integrations/supabase/client";

/**
 * Generate or retrieve session ID for the current browser session
 */
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('momentum_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('momentum_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Get current authenticated user ID if available
 */
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Main tracking function - sends analytics event to the backend
 * Silently fails to avoid breaking the app
 */
export async function track(
  eventName: string, 
  properties?: Record<string, unknown>
): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    
    const { error } = await supabase.functions.invoke("track-analytics", {
      body: {
        eventName,
        properties: properties || {},
        userId,
        sessionId: getSessionId(),
        pageUrl: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
      },
    });

    if (error) {
      console.warn("Analytics tracking failed:", error);
    }
  } catch (error) {
    // Silently fail - don't break the app for analytics
    console.warn("Analytics tracking failed:", error);
  }
}

/**
 * Track when a user hits a feature limit
 */
export function trackLimitHit(
  limitType: 'goal' | 'habit' | 'coach' | 'plan' | 'mentor' | 'integration',
  details?: Record<string, unknown>
): void {
  track(`${limitType}_limit_hit`, details);
}

/**
 * Track upgrade modal interactions
 */
export function trackUpgradeModal(
  action: 'shown' | 'cta_clicked' | 'dismissed',
  trigger: string,
  plan?: 'monthly' | 'annual'
): void {
  track(`upgrade_modal_${action}`, { trigger, plan });
}

/**
 * Track pricing page events
 */
export function trackPricing(
  action: string, 
  details?: Record<string, unknown>
): void {
  track(`pricing_${action}`, details);
}

/**
 * Track page views
 */
export function trackPageView(
  pageName: string, 
  properties?: Record<string, unknown>
): void {
  track('page_viewed', { pageName, ...properties });
}

/**
 * Track goal creation
 */
export function trackGoalCreated(tier: string, goalCount: number): void {
  track('goal_created', { tier, goalCount });
}

/**
 * Track habit creation
 */
export function trackHabitCreated(tier: string, habitCount: number): void {
  track('habit_created', { tier, habitCount });
}

/**
 * Track coach analysis
 */
export function trackCoachAnalysis(tier: string, surface: 'tab' | 'drawer' = 'drawer'): void {
  track('coach_analysis_run', { tier, surface });
}

/**
 * Track upgrade completion
 */
export function trackUpgradeCompleted(plan: 'monthly' | 'annual', amount: number): void {
  track('upgrade_completed', { plan, amount });
}

/**
 * Track invite code generated by admin
 */
export function trackInviteCodeGenerated(details: {
  code: string;
  campaign?: string | null;
  source?: string | null;
  duration_days: number;
  max_uses: number | null;
  admin_id: string;
}): void {
  track('invite_code_generated', details);
}

/**
 * Track invite code successfully redeemed
 */
export function trackInviteCodeRedeemed(details: {
  code: string;
  campaign?: string | null;
  source?: string | null;
  user_id: string;
  duration_days: number;
}): void {
  track('invite_code_redeemed', details);
}

/**
 * Track invite code redemption failure
 */
export function trackInviteCodeFailed(details: {
  code_attempted: string;
  error_type: 'invalid' | 'expired' | 'exhausted' | 'already_redeemed' | 'unknown';
  user_id?: string;
}): void {
  track('invite_code_failed', details);
}

/**
 * Track invite code deactivated by admin
 */
export function trackInviteCodeDeactivated(details: {
  code: string;
  reason?: string;
  admin_id: string;
  total_redemptions: number;
}): void {
  track('invite_code_deactivated', details);
}
